import axios from 'axios';
import { store } from '../store/store';
import { clearAuth, setCredentials } from '../store/slices/authSlice';



const API_BASE_URL = (process.env.REACT_APP_API_URL || 'https://api.trhldev.co.za').trim();

const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 300000, // 5 minutes for large file uploads
    maxContentLength: 100 * 1024 * 1024, // 100MB
    maxBodyLength: 100 * 1024 * 1024, // 100MB
});

// Create a separate axios instance for refresh token requests to avoid interceptors.
const refreshAxiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000, // Shorter timeout for refresh requests.....
});

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve();
        }
    });
    failedQueue = [];
};

axiosInstance.interceptors.request.use(
    (config) => {
        const token = store.getState().auth.accessToken;
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
    }
);

axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Log detailed error information
        console.error('Response error:', {
            status: error.response?.status,
            url: originalRequest?.url,
            method: originalRequest?.method,
            data: error.response?.data
        });

        // If error is not 401 or request has already been retried, reject
        if (error.response?.status !== 401 || originalRequest._retry) {
            return Promise.reject(error);
        }

        if (isRefreshing) {
            // If token refresh is in progress, queue the request
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            })
                .then(() => {
                    return axiosInstance(originalRequest);
                })
                .catch(err => {
                    return Promise.reject(err);
                });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
            const refreshTokenValue = store.getState().auth.refreshToken;
            if (!refreshTokenValue) {
                throw new Error('No refresh token available');
            }

            console.log('Attempting to refresh token...');

            // Direct axios call instead of using the intercepted instance
            const response = await refreshAxiosInstance.post('/auth/api/v1/refresh', {
                refreshToken: refreshTokenValue
            });

            if (response.data) {
                console.log('Token refresh successful');
                // Store new tokens
                store.dispatch(setCredentials(response.data));
                // Update authorization header for the original request
                originalRequest.headers.Authorization = `Bearer ${response.data.accessToken}`;
                processQueue();
                return axiosInstance(originalRequest);
            } else {
                throw new Error('Failed to refresh token: empty response');
            }
        } catch (refreshError: any) {
            console.error('Token refresh failed:', refreshError.message || refreshError);
            processQueue(refreshError);
            store.dispatch(clearAuth());
            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }
);

export const customAxiosInstanceMutator = async <T>(config: any): Promise<T> => {
    // Keep draft binding outside the generated client: Loan expects this correlation header.
    if (config.url === '/loan/api/v1/loan-applications' && config.method?.toUpperCase() === 'POST'
        && config.data?.loanType === 'PERSONAL' && config.data.statementCaptureDraftId) {
        const { statementCaptureDraftId, ...application } = config.data;
        config = { ...config, data: application,
            headers: { ...config.headers, 'X-Tirhelo-Statement-Draft': statementCaptureDraftId } };
    }
    try {
        const response = await axiosInstance(config);
        return response.data;
    } catch (error) {
        console.error('API request failed:', error);
        throw error;
    }
};
